// src/components/FloorManagement/FloorManagementUtils.js
import { jwtDecode } from "jwt-decode";
import isAccess from "./checkAccess.js";
import {
  deleteFloor,
  updatedFloorId,
  downloadFloorBlueprint
} from "../Actions/FloorManagementAction";
import React from "react";
import AccessibleTooltip from "../Component/Tooltip/Tooltip";
import EditIcon from "../assets/images/editIcon.svg";
import DeleteIcon from "../assets/images/DeleteIcon.svg";
import addNew from "../assets/images/addNew.svg";
import downloadButton from "../assets/images/downloadButton.svg";

export const decodeUserAccess = (token) => {
  const decodedToken = jwtDecode(token);
  const { edit, delete: canDelete, view, create } = isAccess(
    decodedToken.userRole,
    "Floor Management"
  );
  return { edit, canDelete, view, create };
};

export const processFloorData = (data) => {
  if (!Array.isArray(data)) return [];
  return data
    .filter((row) => !row.isDeleted)
    .map((row) => ({
      id: row.id,
      buildingId: row.buildingId || "N/A",
      floor: row.floorName || "N/A",
      floorPlan: row.floorImageUrl || "N/A",
      ConfigurationAdministrator:
        Array.isArray(row.floorUsers) && row.floorUsers.length > 0
          ? row.floorUsers.map((user) => user.name)
          : "N/A",
      customerNameId: row.customer
        ? `${row.customer.firstName.trim()} ${row.customer.lastName.trim()} - ${row.customer.customerId}`
        : "N/A",
      sourceType: row.sourceType,
    }));
};

export const handleEditUtility = (setSelectedEditid, setEditData, setActionButton, setOpen, deviceData, selectedEditId) => {
  setSelectedEditid(selectedEditId);
  setEditData(deviceData);
  setActionButton("edit");
  setOpen(true);
};

export const handleDeleteUtility = (dispatch, id, setOpenToast, setMessage, createFloorBtnRef, filters) => {
  dispatch(deleteFloor(id, filters));
  setOpenToast(true);
  setMessage("Deleted successfully.");
  if (createFloorBtnRef.current) {
    setTimeout(() => {
      createFloorBtnRef.current.focus();
    }, 400);
  }
};

export const handleToastCloseUtility = (setOpenToast, setMessage) => {
  setOpenToast(false);
  setMessage("");
};

export const downloadBlueprintUtility = (dispatch, id) => {
  dispatch(downloadFloorBlueprint(id));
};

export const goToAddDevicesUtility = (dispatch, navigate, id) => {
  dispatch(updatedFloorId(id));
  navigate(`/floorManagement/assignDevices/${id}`);
};

export const getFloorColumns = ({
  edit = false,
  canDelete = false,
  view = false,
  onEdit = () => {},
  onRequestDelete = () => {},
  onView = () => {},
  onDownload = () => {},
}) => {
  const baseColumns = [
    { field: "buildingId", headerName: "Building ID", flex: 1, sortable: true },
    { field: "floor", headerName: "Floor Name", flex: 1, sortable: true },
    {
      field: "floorPlan",
      headerName: "Floor Plan",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <a
          role="link"
          aria-label="Blueprint (opens in new tab)"
          href={params.row.floorPlan}
          target="_blank"
          rel="noopener noreferrer"
          className="blurPrintLink"
        >
          Blueprint
        </a>
      ),
    },
    {
      field: "customerNameId",
      headerName: "Customer Name & ID",
      flex: 1,
      renderCell: (params) => <div>{params.row.customerNameId || "N/A"}</div>,
    },
    {
      field: "sourceType",
      headerName: "Source Type",
      flex: 1,
      renderCell: (params) => <div>{params.row.sourceType || "N/A"}</div>,
    },
  ];

  // If no action permissions, return only base columns
  if (!edit && !canDelete && !view) return baseColumns;

  // Actions column
  const actionsColumn = {
    field: "Actions",
    headerName: "Actions",
    flex: 1,
    align: "right",
    headerAlign: "right",
    sortable: false,
    renderCell: (params) => {
      const row = params.row;
      return (
        <div style={{ display: "flex", gap: "10px" }} role="list">
          {edit && row.sourceType !== "IOT" && (
            <div role="listitem">
              <AccessibleTooltip title="Edit">
                <button
                  className="icon-btn edit-icon-btn"
                  aria-label={`Edit ${row.floor || "floor"}`}
                  onClick={() => onEdit(row, row.id)}
                >
                  <img src={EditIcon} alt="" aria-hidden="true" />
                </button>
              </AccessibleTooltip>
            </div>
          )}

          {canDelete && row.sourceType !== "IOT" && (
            <div role="listitem">
              <AccessibleTooltip title="Delete">
                <button
                  className="icon-btn delete-icon-btn"
                  aria-label={`Delete ${row.floor || "floor"}`}
                  onClick={() => onRequestDelete(row.id)}
                >
                  <img src={DeleteIcon} alt="" aria-hidden="true" />
                </button>
              </AccessibleTooltip>
            </div>
          )}

          {view && row.sourceType !== "IOT" && (
            <div role="listitem">
              <AccessibleTooltip title="View">
                <button
                  className="icon-btn"
                  aria-label={`View devices for ${row.floor || "floor"}`}
                  onClick={() => onView(row.id)}
                  data-testid="add-new-icon"
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                >
                  <img src={addNew} alt="" aria-hidden="true" style={{ width: 16, height: 14 }} />
                </button>
              </AccessibleTooltip>
            </div>
          )}

          {view && row.sourceType === "IOT" && (
            <div role="listitem">
              <AccessibleTooltip title="Download">
                <button
                  className="icon-btn"
                  aria-label={`Download blueprint for ${row.floor || "floor"}`}
                  onClick={() => onDownload(row.id)}
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                >
                  <img src={downloadButton} alt="" aria-hidden="true" style={{ width: 16, height: 16 }} />
                </button>
              </AccessibleTooltip>
            </div>
          )}
        </div>
      );
    },
  };

  return [...baseColumns, actionsColumn];
};